import React, { useEffect, useState } from 'react';
import { LuArrowRight, LuRepeat } from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';

import { TRANSFER_PROVIDER_LABEL, TransferJob } from '../../entities/transfer';
import { listTransferJobs } from '../../shared/api/transfer';

const STATUS_BADGE: Record<TransferJob['status'], string> = {
  reading: 'badge-info',
  mapping_proposed: 'badge-warning',
  mapping_confirmed: 'badge-info',
  transferring: 'badge-info',
  completed: 'badge-success',
  failed: 'badge-error',
};

const TransferJobsListPage: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<TransferJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    listTransferJobs()
      .then(setJobs)
      .catch(() => setError('Failed to load transfer jobs.'))
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-base-200">
      <div className="p-4 space-y-4">
        <div>
          <div className="text-2xl font-semibold flex items-center gap-2">
            <LuRepeat className="opacity-70" /> Project transfers
          </div>
          <div className="text-sm opacity-70">
            Copy a whole project between Jira, Trello, and Standalone. Start one from a project&apos;s page.
          </div>
        </div>

        {error && (
          <div className="alert alert-error">
            <span>{error}</span>
          </div>
        )}

        {isLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="skeleton h-16 w-full rounded-xl" />
            ))}
          </div>
        ) : jobs.length === 0 ? (
          <div className="card bg-base-100 shadow-card">
            <div className="card-body p-8 items-center text-center gap-2">
              <LuRepeat size={32} className="opacity-30" />
              <div className="text-sm opacity-60">No transfers yet.</div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            {jobs.map((job) => (
              <div
                key={job._id}
                className="card bg-base-100 shadow-card hover:shadow-md transition-shadow cursor-pointer"
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/transfer/${job._id}`)}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/transfer/${job._id}`)}
              >
                <div className="card-body p-4 flex-row items-center gap-3">
                  <div className="flex items-center gap-2 min-w-0 flex-1">
                    <span className="font-medium truncate">{job.sourceProjectName ?? job.sourceProjectId}</span>
                    <LuArrowRight className="opacity-40 shrink-0" size={14} />
                    <span className="truncate">{job.targetProjectName}</span>
                  </div>
                  <span className="text-xs opacity-50 shrink-0">
                    {TRANSFER_PROVIDER_LABEL[job.sourceProvider]} → {TRANSFER_PROVIDER_LABEL[job.targetProvider]}
                  </span>
                  <span className={`badge badge-sm shrink-0 ${STATUS_BADGE[job.status]}`}>{job.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransferJobsListPage;
